import SpeakerListAPI from "../serviceAPI/SpeakerListAPI";
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import UploadAPI from "../serviceAPI/UploadAPI";

const initialState = {
  speakerData: [],
  speakerOptions: [],
  attendeeOptions: [],
  attendeeData: [],
  visitSpeakerData: [],
  visitAttendeeData: [],
  visitSpeakerOptions: [],
  visitAttendeeOptions: [],
  isLoading: false,
  prevFileDetails: null,
  prevPhotoId: "",
  prevMediaFileDetails: null,
  prevMediaId: "",
  videoStatus: null,
  videoUrl: "",
  mediaId: 0,
  mediaProgress: 0,
  selectedMediaFile: null,
  prevFileType: null,
  errorCode: null,

  totalSpeakers: 0,
  totalAttendees: 0,
  speakersLoading: false,
  attendeesLoading: false,

  deleteSuccess: false,

  emailCheckLoading: false,
  emailCheckData: null,
  emailCheckError: null,
};

export const getAllSpeakers = createAsyncThunk(
  "getSpeakers",
  async ({ officeId, page, limit, search }, { rejectWithValue }) => {
    try {
      const res = await SpeakerListAPI.GetAllSpeakers({
        officeId,
        page,
        limit,
        search,
      });
      return res;
    } catch (error) {
      return rejectWithValue(error.response?.status || error.message);
    }
  }
);

export const getAllSpeakersOfVisit = createAsyncThunk(
  "getVisitSpeakers",
  async (visitId, { rejectWithValue }) => {
    try {
      const res = await SpeakerListAPI.GetAllSpeakersOfVisit(visitId);
      return res;
    } catch (error) {
      return rejectWithValue(error.response?.status || error.message);
    }
  }
);

export const getAllAttendeesOfVisit = createAsyncThunk(
  "getVisitAttendees",
  async (visitId, { rejectWithValue }) => {
    try {
      const res = await SpeakerListAPI.GetAllAttendeesOfVisit(visitId);
      return res;
    } catch (error) {
      return rejectWithValue(error.response?.status || error.message);
    }
  }
);

export const createNewSpeaker = createAsyncThunk(
  "createNewSpeaker",
  async ({ newData, id }, { rejectWithValue }) => {
    try {
      const res = await SpeakerListAPI.CreateNewSpeaker(newData, id);
      return res;
    } catch (error) {
      return rejectWithValue(error.response?.status || error.message);
    }
  }
);

export const createNewAttendee = createAsyncThunk(
  "createNewAttendee",
  async ({ newData, id }, { rejectWithValue }) => {
    try {
      const res = await SpeakerListAPI.CreateNewAttendee(newData, id);
      return res;
    } catch (error) {
      return rejectWithValue(error.response?.status || error.message);
    }
  }
);

export const getAllAttendes = createAsyncThunk(
  "getAttendes",
  async ({ officeId, page, limit, search }, { rejectWithValue }) => {
    try {
      const res = await SpeakerListAPI.GetAllAttendes({
        officeId,
        page,
        limit,
        search,
      });
      return res;
    } catch (error) {
      return rejectWithValue(error.response?.status || error.message);
    }
  }
);

export const updateAttendee = createAsyncThunk(
  "updateAttendee",
  async ({ newData, id }, { rejectWithValue }) => {
    try {
      const res = await SpeakerListAPI.UpdateAttendee(newData, id);
      return res;
    } catch (error) {
      return rejectWithValue(error.response?.status || error.message);
    }
  }
);

export const deleteAttendee = createAsyncThunk(
  "deleteAttendee",
  async (id, { rejectWithValue }) => {
    try {
      const res = await SpeakerListAPI.DeleteAttendee(id);
      return res;
    } catch (error) {
      return rejectWithValue(error.response?.status || error.message);
    }
  }
);

export const audioFileUpload = createAsyncThunk(
  "audioFileUpload",
  async (data, { dispatch, rejectWithValue }) => {
    try {
      const res = await UploadAPI.UploadNewAudioFile(data, dispatch);
      return res;
    } catch (error) {
      return rejectWithValue(error.response?.status || error.message);
    }
  }
);

export const updateSpeaker = createAsyncThunk(
  "updateSpeaker",
  async ({ newData, id }, { rejectWithValue }) => {
    try {
      const res = await SpeakerListAPI.UpdateSpeaker(newData, id);
      return res;
    } catch (error) {
      return rejectWithValue(error.response?.status || error.message);
    }
  }
);

export const videoMediaFileUpload = createAsyncThunk(
  "videoMediaFileUpload",
  async (data, { rejectWithValue }) => {
    try {
      const res = await UploadAPI.UploadVideoFile(data);
      return res;
    } catch (error) {
      return rejectWithValue(error.response?.status || error.message);
    }
  }
);

export const deleteSpeaker = createAsyncThunk(
  "deleteSpeaker",
  async (id, { rejectWithValue }) => {
    try {
      const res = await SpeakerListAPI.DeleteSpeaker(id);
      return res;
    } catch (error) {
      return rejectWithValue(error.response?.status || error.message);
    }
  }
);

export const fileMediaDelete = createAsyncThunk(
  "fileMediaDelete",
  async (id, { dispatch, rejectWithValue }) => {
    try {
      if (id.id === "" || id.id === null) {
        return;
      }
      const res = await UploadAPI.DeleteUploadedMediaFile(id, dispatch);
      return res;
    } catch (error) {
      return rejectWithValue(error.response?.status || error.message);
    }
  }
);

export const checkUserEmailInAnotherVisit = createAsyncThunk(
  "checkUserEmailInAnotherVisit",
  async ({ email }, { rejectWithValue }) => {
    try {
      const res = await SpeakerListAPI.CheckUserAvailability(email);
      return res;
    } catch (error) {
      return rejectWithValue(error.response?.status || error.message);
    }
  }
);

const SpeakerSlice = createSlice({
  name: "speakers",
  initialState,
  reducers: {
    setPreviousFileDetails: (state, action) => {
      state.prevFileDetails = action.payload;
    },
    setPrevPhotoId: (state, action) => {
      state.prevPhotoId = action.payload;
    },
    setPreviousMediaFileDetails: (state, action) => {
      state.prevMediaFileDetails = action.payload;
    },
    setPrevMediaId: (state, action) => {
      state.prevMediaId = action.payload;
    },
    mediaProgressPercentage: (state, action) => {
      state.mediaProgress = action.payload;
    },
    setMediaSelectedFile: (state, action) => {
      state.selectedMediaFile = action.payload;
    },
    cancelVideoUpload: (state, action) => {
      state.videoStatus = null;
      state.videoUrl = "";
    },
    setPrevFileType: (state, action) => {
      state.prevFileType = action.payload;
    },
    resetErrorAlert: (state, action) => {
      state.errorCode = null;
    },
    resetEmailCheckData: (state) => {
      state.emailCheckLoading = false;
      state.emailCheckData = null;
      state.emailCheckError = null;
    },
    resetDeleteSuccess: (state) => {
      state.deleteSuccess = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getAllSpeakers.pending, (state) => {
        state.speakersLoading = true;
      })
      .addCase(getAllSpeakers.fulfilled, (state, action) => {
        state.speakersLoading = false;
        const speakers = action.payload.speakers || [];
        const totalCount = action.payload.totalSpeakers || 0;
        state.speakerData = [...speakers];
        state.totalSpeakers = totalCount;
        state.speakerOptions = speakers.map((speaker) => ({
          value: speaker._id,
          label: speaker.name,
        }));
      })
      .addCase(getAllSpeakers.rejected, (state, action) => {
        state.speakersLoading = false;
        state.errorCode = action.payload;
      })

      .addCase(getAllAttendes.pending, (state) => {
        state.attendeesLoading = true;
      })
      .addCase(getAllAttendes.fulfilled, (state, action) => {
        state.attendeesLoading = false;
        const attendees = action.payload.attendees || [];
        const totalCount = action.payload.totalAttendees || attendees.length;
        state.attendeeData = [...attendees];
        state.totalAttendees = totalCount;
        state.attendeeOptions = attendees.map((attendee) => ({
          value: attendee._id,
          label: attendee.name,
        }));
      })
      .addCase(getAllAttendes.rejected, (state, action) => {
        state.attendeesLoading = false;
        state.errorCode = action.payload;
      })

      .addCase(getAllSpeakersOfVisit.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getAllSpeakersOfVisit.fulfilled, (state, action) => {
        state.isLoading = false;
        const speakers = action.payload || [];
        state.visitSpeakerData = [...speakers];
        state.visitSpeakerOptions = speakers.map((speaker) => ({
          value: speaker._id,
          label: speaker.name,
        }));
      })
      .addCase(getAllSpeakersOfVisit.rejected, (state, action) => {
        state.isLoading = false;
        state.errorCode = action.payload;
      })

      .addCase(getAllAttendeesOfVisit.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getAllAttendeesOfVisit.fulfilled, (state, action) => {
        state.isLoading = false;
        state.visitAttendeeData = [...action.payload];
        state.visitAttendeeOptions = action.payload.map((attendee) => ({
          value: attendee._id,
          label: attendee.name,
        }));
      })
      .addCase(getAllAttendeesOfVisit.rejected, (state, action) => {
        state.isLoading = false;
        state.errorCode = action.payload;
      })

      // createNewSpeaker
      .addCase(createNewSpeaker.fulfilled, (state, action) => {
        state.speakerOptions = [
          ...state.speakerOptions,
          { value: action.payload._id, label: action.payload.name },
        ];
        state.speakerData.push(action.payload);
      })
      .addCase(createNewSpeaker.rejected, (state, action) => {
        state.errorCode = action.payload;
      })

      // createNewAttendee
      .addCase(createNewAttendee.fulfilled, (state, action) => {
        state.attendeeOptions = [
          ...state.attendeeOptions,
          { value: action.payload._id, label: action.payload.name },
        ];
        state.attendeeData.push(action.payload);
      })
      .addCase(createNewAttendee.rejected, (state, action) => {
        state.errorCode = action.payload;
      })

      // updateSpeaker
      .addCase(updateSpeaker.fulfilled, (state, action) => {
        state.speakerData = state.speakerData.map((speaker) =>
          speaker._id === action.payload._id ? action.payload : speaker
        );
        state.speakerOptions = state.speakerOptions.map((speaker) =>
          speaker.value === action.payload._id
            ? { value: action.payload._id, label: action.payload.name }
            : speaker
        );
      })
      .addCase(updateSpeaker.rejected, (state, action) => {
        state.errorCode = action.payload;
      })

      // updateAttendee
      .addCase(updateAttendee.fulfilled, (state, action) => {
        state.attendeeData = state.attendeeData.map((attendee) =>
          attendee._id === action.payload._id ? action.payload : attendee
        );
        state.attendeeOptions = state.attendeeOptions.map((attendee) =>
          attendee.value === action.payload._id
            ? { value: action.payload._id, label: action.payload.name }
            : attendee
        );
      })
      .addCase(updateAttendee.rejected, (state, action) => {
        state.errorCode = action.payload;
      })

      // deleteAttendee
      .addCase(deleteAttendee.fulfilled, (state, action) => {
        const id = action.payload._id;
        const index = state.attendeeData.map((item) => item._id).indexOf(id);
        if (index !== -1) {
          state.attendeeData.splice(index, 1);
        }
        state.totalAttendees = Math.max(0, state.totalAttendees - 1);
        state.deleteSuccess = true;
      })
      .addCase(deleteAttendee.rejected, (state, action) => {
        state.errorCode = action.payload;
      })

      // deleteSpeaker
      .addCase(deleteSpeaker.fulfilled, (state, action) => {
        const id = action.payload._id;
        const index = state.speakerData.map((item) => item._id).indexOf(id);
        if (index !== -1) {
          state.speakerData.splice(index, 1);
        }
        state.totalSpeakers = Math.max(0, state.totalSpeakers - 1);
        state.deleteSuccess = true;
      })
      .addCase(deleteSpeaker.rejected, (state, action) => {
        state.errorCode = action.payload;
      })

      // videoMediaFileUpload
      .addCase(videoMediaFileUpload.pending, (state) => {
        state.videoStatus = "pending";
      })
      .addCase(videoMediaFileUpload.fulfilled, (state, action) => {
        state.mediaId = action.payload[0]._id;
        state.videoStatus = action.payload[0].fileUploadStatus;
      })
      .addCase(videoMediaFileUpload.rejected, (state, action) => {
        state.errorCode = action.payload;
      })

      // audioFileUpload
      .addCase(audioFileUpload.fulfilled, (state, action) => {
        state.mediaId = action.payload[0]._id;
      })
      .addCase(audioFileUpload.rejected, (state, action) => {
        state.errorCode = action.payload;
      })

      // fileMediaDelete
      .addCase(fileMediaDelete.fulfilled, (state) => {
        state.videoStatus = null;
        state.videoUrl = "";
      })
      .addCase(fileMediaDelete.rejected, (state, action) => {
        state.errorCode = action.payload;
      })

      // checkUserEmailInAnotherVisit
      .addCase(checkUserEmailInAnotherVisit.pending, (state) => {
        state.emailCheckLoading = true;
        state.emailCheckError = null;
        state.emailCheckData = null;
      })
      .addCase(checkUserEmailInAnotherVisit.fulfilled, (state, action) => {
        state.emailCheckLoading = false;
        state.emailCheckData = action.payload;
      })
      .addCase(checkUserEmailInAnotherVisit.rejected, (state, action) => {
        state.emailCheckLoading = false;
        state.emailCheckError = action.payload;
        state.emailCheckData = null;
      });
  },
});

export const {
  setPreviousFileDetails,
  setPrevPhotoId,
  setPreviousMediaFileDetails,
  setPrevMediaId,
  mediaProgressPercentage,
  setMediaSelectedFile,
  cancelVideoUpload,
  setPrevFileType,
  resetErrorAlert,
  resetEmailCheckData,
  resetDeleteSuccess,
} = SpeakerSlice.actions;

export default SpeakerSlice.reducer;